import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPaidVendors,
  lookupVendors,
  addPaidVendors,
  updatePaidVendor,
  removePaidVendor,
  removePaidVendors,
} from "../api/paid-vendors";

const PAID_VENDORS_KEY = "paidVendors";
const VENDOR_LOOKUP_KEY = "vendorLookup";

/**
 * Paginated paid-vendor list. `keepPreviousData` keeps the current rows on
 * screen while the next page or a new search term loads, so the table doesn't
 * flash empty on every keystroke.
 */
export const usePaidVendors = (params) =>
  useQuery({
    queryKey: [PAID_VENDORS_KEY, params],
    queryFn: () => getPaidVendors(params),
    keepPreviousData: true,
  });

/** Vendor search for the picker. `enabled` lets callers hold off until open. */
export const useVendorLookup = (search, enabled = true) =>
  useQuery({
    queryKey: [VENDOR_LOOKUP_KEY, search],
    queryFn: () => lookupVendors(search),
    enabled,
    keepPreviousData: true,
    staleTime: 30_000,
  });

/**
 * Both lists change on every write: the paid list gains/loses rows and the
 * lookup's `isPaid` flags go stale, so they are invalidated together.
 */
const useWriteMutation = (mutationFn) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAID_VENDORS_KEY] });
      queryClient.invalidateQueries({ queryKey: [VENDOR_LOOKUP_KEY] });
    },
  });
};

export const useAddPaidVendors = () => useWriteMutation(addPaidVendors);

/**
 * `vendorId` addresses the entry; `body` may itself carry a `vendorId` to move
 * the entry onto a different vendor, so the two are kept apart deliberately.
 */
export const useUpdatePaidVendor = () =>
  useWriteMutation(({ vendorId, body }) => updatePaidVendor(vendorId, body));

/** One entry point for both flows — a single row and a bulk selection. */
export const useRemovePaidVendors = () =>
  useWriteMutation((vendorIds) =>
    vendorIds.length === 1
      ? removePaidVendor(vendorIds[0])
      : removePaidVendors(vendorIds)
  );
